import { supabase, isPreviewMode } from './supabase';
import { logger } from './logger';

export const auth = {
  getCurrentSession: async () => {
    try {
      // In preview mode, return a mock session
      if (isPreviewMode()) {
        return {
          user: { 
            id: 'preview-user', 
            email: 'preview@example.com',
            user_metadata: { name: 'Preview User' }
          }
        };
      }
      
      const sessionQuery = supabase.auth.getSession();
      const { data: { session }, error } = await sessionQuery;
      if (error) throw error;
      return session;
    } catch (error) {
      logger.error('Error getting session:', error);
      return null;
    }
  },

  signIn: async (email: string, password: string) => {
    try {
      // Validate inputs
      if (!email?.trim() || !password?.trim()) {
        throw new Error('Please enter both email and password');
      }

      // In preview mode, return a mock session
      if (isPreviewMode()) {
        return {
          user: { 
            id: 'preview-user', 
            email: email,
            user_metadata: { name: 'Preview User' }
          }
        };
      }

      const signInQuery = supabase.auth.signInWithPassword({
        email: email.trim(),
        password: password.trim()
      });
      
      const { data, error } = await signInQuery;

      if (error) {
        // Handle specific error cases
        if (error.message?.includes('Invalid login credentials')) {
          throw new Error('Invalid email or password. Please try again.');
        }
        if (error.message?.includes('rate limit')) {
          throw new Error('Too many login attempts. Please try again in a few minutes.');
        }
        throw error;
      }

      return data.session;
    } catch (error: any) {
      logger.error('Sign in error:', error);
      throw new Error(error.message || 'Failed to sign in. Please check your credentials.');
    }
  },

  signInWithGoogle: async () => {
    try {
      if (isPreviewMode()) {
        return {
          user: {
            id: 'preview-user',
            email: 'preview@example.com',
            user_metadata: { name: 'Preview User' }
          }
        };
      }

      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/app`,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          }
        }
      });

      if (error) throw error;
      return data;
    } catch (error: any) {
      logger.error('Google sign in error:', error);
      throw new Error(error.message || 'Failed to sign in with Google.');
    }
  },

  signOut: async () => {
    try {
      // Clear any auth-dependent state first
      window.localStorage.clear(); // Clear all local storage
      sessionStorage.clear(); // Clear all session storage

      // In preview mode, just redirect
      if (isPreviewMode()) {
        window.location.href = '/';
        return;
      }

      // Attempt to sign out from Supabase
      const signOutQuery = supabase.auth.signOut();
      const { error } = await signOutQuery;
      if (error) {
        logger.error('Supabase sign out error:', error);
      }

      // Redirect to home page
      window.location.href = '/';
    } catch (error) {
      logger.error('Sign out error:', error);
      // Still redirect to home page even if there's an error
      window.location.href = '/';
    }
  }
};